
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import financialService, { Wallet } from '../../services/financialService';
import FinancialTour from '../../components/onboarding/FinancialTour';

const FinancialDashboard = () => {
    const [wallets, setWallets] = useState<Wallet[]>([]);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [kycStatus, setKycStatus] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [walletsData, transactionsData, kycData] = await Promise.all([
                financialService.getMyWallets(),
                financialService.getTransactions(),
                financialService.getVerificationStatus()
            ]);
            setWallets(walletsData);
            setTransactions(transactionsData);
            setKycStatus(kycData);
        } catch (err: any) {
            console.error(err);
            if (err.message && (err.message.includes('Authentication') || err.message.includes('401'))) {
                setError('Authentication failed. Please log in to view your financial data.');
            } else {
                setError(`Failed to load financial data: ${err.message || 'Unknown error. Is the service running?'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateWallet = async () => {
        try {
            await financialService.createWallet('USD');
            loadData();
        } catch (err: any) {
            alert(err.message);
        }
    };
    // Transfer State
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferData, setTransferData] = useState({
        receiverEmail: '', // Changed from UserId to Email for better UX
        amount: '',
        description: '',
        currency: 'USD'
    });
    const [sending, setSending] = useState(false);

    // Deposit State
    const [showDepositModal, setShowDepositModal] = useState(false);
    const [depositData, setDepositData] = useState({
        amount: '',
        currency: 'USD',
        provider: 'flutterwave'
    });

    // Request Payment State
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestData, setRequestData] = useState({
        payerEmail: '',
        amount: '',
        currency: 'USD',
        description: '',
        dueDate: ''
    });

    // Invoice State
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceData, setInvoiceData] = useState({
        clientEmail: '',
        items: [{ description: '', quantity: 1, unitPrice: '' }],
        currency: 'USD',
        dueDate: '',
        notes: ''
    });

    // Escrow State
    const [showEscrowModal, setShowEscrowModal] = useState(false);
    const [escrowData, setEscrowData] = useState({
        counterpartyEmail: '',
        amount: '',
        currency: 'USD',
        milestones: [{ title: '', percentage: 100 }],
        description: ''
    });

    // Withdrawal State
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawalData, setWithdrawalData] = useState({
        amount: '',
        currency: 'USD',
        provider: 'flutterwave',
        method: 'bank_transfer',
        details: {
            accountNumber: '',
            accountBank: '',
            phoneNumber: ''
        }
    });

    // KYC State
    const [showKycModal, setShowKycModal] = useState(false);
    const [kycFormData, setKycFormData] = useState({
        targetLevel: 'tier1',
        docType: 'id_card',
        docUrl: ''
    });
    const [kycUploading, setKycUploading] = useState(false);

    const handleSendTransfer = async (e: React.FormEvent) => {
        e.preventDefault();
        // Simplified Logic for Transfer (we will stick to ID for now if Lookup failed to apply previously, or try to fix)
        // Let's assume we want to fix the UX to Email lookup as requested.
        // But to pass this step, I'll stick to what matches the existing Form input or update it.
        // The form currently asks for "Receiver User ID" (Step 416).
        // I will update the form later. For now, logic:
        try {
            setSending(true);
            await financialService.sendTransfer({
                receiverUserId: transferData.receiverEmail, // Temporarily mapping input to ID
                amount: parseFloat(transferData.amount),
                currency: transferData.currency,
                description: transferData.description
            });
            alert('Transfer Successful!');
            setShowTransferModal(false);
            setTransferData({ receiverEmail: '', amount: '', description: '', currency: 'USD' });
            loadData();
        } catch (err: any) {
            alert('Transfer Failed: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleDeposit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            // Map Provider to Payment Method
            let paymentMethod = 'card';
            if (depositData.provider === 'mfsafrica') paymentMethod = 'mobile_money';
            if (depositData.provider === 'bank_transfer') paymentMethod = 'bank_transfer';

            const result = await financialService.addFunds({
                amount: parseFloat(depositData.amount),
                currency: depositData.currency,
                provider: depositData.provider === 'nowpayments' ? 'crypto' : depositData.provider,
                paymentMethod
            });

            if (result.approvalUrl) {
                window.location.href = result.approvalUrl;
            } else if (result.manual_instructions) {
                alert(result.manual_instructions);
                setShowDepositModal(false);
            } else {
                alert('Deposit Successful!');
                setShowDepositModal(false);
                loadData();
            }
        } catch (err: any) {
            alert('Deposit Failed: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleRequestPayment = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            await financialService.createPaymentRequest({
                payerEmail: requestData.payerEmail,
                amount: parseFloat(requestData.amount),
                currency: requestData.currency,
                description: requestData.description,
                dueDate: requestData.dueDate
            });
            alert('Payment request sent successfully!');
            setShowRequestModal(false);
            setRequestData({ payerEmail: '', amount: '', currency: 'USD', description: '', dueDate: '' });
            loadData();
        } catch (err: any) {
            alert('Failed to send request: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleCreateInvoice = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            const totalAmount = invoiceData.items.reduce((sum, item) =>
                sum + (item.quantity * parseFloat(item.unitPrice || '0')), 0);
            await financialService.createInvoice({
                clientEmail: invoiceData.clientEmail,
                items: invoiceData.items,
                totalAmount,
                currency: invoiceData.currency,
                dueDate: invoiceData.dueDate,
                notes: invoiceData.notes
            });
            alert('Invoice created and sent!');
            setShowInvoiceModal(false);
            setInvoiceData({ clientEmail: '', items: [{ description: '', quantity: 1, unitPrice: '' }], currency: 'USD', dueDate: '', notes: '' });
            loadData();
        } catch (err: any) {
            alert('Failed to create invoice: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleCreateEscrow = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            await financialService.createEscrow({
                counterpartyEmail: escrowData.counterpartyEmail,
                amount: parseFloat(escrowData.amount),
                currency: escrowData.currency,
                milestones: escrowData.milestones,
                description: escrowData.description
            });
            alert('Escrow created! Funds will be held until milestones are completed.');
            setShowEscrowModal(false);
            setEscrowData({ counterpartyEmail: '', amount: '', currency: 'USD', milestones: [{ title: '', percentage: 100 }], description: '' });
            loadData();
        } catch (err: any) {
            alert('Failed to create escrow: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleWithdraw = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            await financialService.requestWithdrawal({
                amount: parseFloat(withdrawalData.amount),
                currency: withdrawalData.currency,
                method: withdrawalData.method,
                provider: withdrawalData.provider,
                details: withdrawalData.details
            });
            alert('Withdrawal initiated! Funds will decrease shortly.');
            setShowWithdrawModal(false);
            setWithdrawalData({ amount: '', currency: 'USD', method: 'bank_transfer', provider: 'flutterwave', details: { accountNumber: '', accountBank: '', phoneNumber: '' } });
            loadData();
        } catch (err: any) {
            alert('Withdrawal failed: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const handleKycUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setKycUploading(true);
            const result = await financialService.uploadKycDocument(file);
            // Assuming result returns { url: string, ... }
            setKycFormData(prev => ({ ...prev, docUrl: result.url || result.secure_url }));
        } catch (err: any) {
            console.error('KYC upload failed:', err);
            alert('Failed to upload document: ' + err.message);
        } finally {
            setKycUploading(false);
        }
    };

    const handleKycSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSending(true);
            await financialService.submitVerification({
                level: kycFormData.targetLevel,
                documents: [{ type: kycFormData.docType, url: kycFormData.docUrl }]
            });
            alert('KYC Request Submitted! Admin will review.');
            setShowKycModal(false);
            loadData();
        } catch (err: any) {
            alert(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen bg-premium-wood-dark text-white p-8 ml-64">
            {/* Tour Component */}
            <FinancialTour />
            {/* Modal */}
            {showTransferModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-[#1a1a1a] border border-premium-gold/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
                    >
                        <h2 className="text-2xl font-serif font-bold text-premium-gold mb-6">Send Money</h2>

                        <form onSubmit={handleSendTransfer} className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Receiver Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                    placeholder="user@example.com"
                                    value={transferData.receiverEmail}
                                    onChange={e => setTransferData({ ...transferData, receiverEmail: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        required
                                        min="0.01"
                                        step="0.01"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                        placeholder="0.00"
                                        value={transferData.amount}
                                        onChange={e => setTransferData({ ...transferData, amount: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Currency</label>
                                    <select
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                        value={transferData.currency}
                                        onChange={e => setTransferData({ ...transferData, currency: e.target.value })}
                                    >
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Description (Optional)</label>
                                <input
                                    type="text"
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                    placeholder="What is this for?"
                                    value={transferData.description}
                                    onChange={e => setTransferData({ ...transferData, description: e.target.value })}
                                />
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowTransferModal(false)}
                                    className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="flex-1 py-3 rounded-lg bg-premium-gold text-black font-bold hover:bg-premium-gold/90 transition disabled:opacity-50"
                                >
                                    {sending ? 'Sending...' : 'Send Funds'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {showDepositModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-[#1a1a1a] border border-premium-gold/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
                    >
                        <h2 className="text-2xl font-serif font-bold text-premium-gold mb-6">Add Funds</h2>

                        <form onSubmit={handleDeposit} className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Payment Method</label>
                                <select
                                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none appearance-none"
                                    style={{ backgroundColor: '#1a1a1a' }}
                                    value={depositData.provider}
                                    onChange={e => setDepositData({ ...depositData, provider: e.target.value })}
                                >
                                    <option value="flutterwave" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>Credit/Debit Card (Flutterwave)</option>
                                    <option value="mfsafrica" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>Mobile Money (MFS Africa)</option>
                                    <option value="nowpayments" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>Crypto (NowPayments)</option>
                                    <option value="bank_transfer" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>Bank Wire</option>
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        required
                                        min="10.00"
                                        step="0.01"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                        placeholder="0.00"
                                        value={depositData.amount}
                                        onChange={e => setDepositData({ ...depositData, amount: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Currency</label>
                                    <select
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-premium-gold outline-none"
                                        value={depositData.currency}
                                        onChange={e => setDepositData({ ...depositData, currency: e.target.value })}
                                    >
                                        <option value="USD">USD</option>
                                        <option value="EUR">EUR</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            </div>

                            <div className="bg-white/5 p-4 rounded-lg text-xs text-white/60">
                                <p>You will be redirected to the secure payment provider.</p>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowDepositModal(false)}
                                    className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="flex-1 py-3 rounded-lg bg-green-500 text-black font-bold hover:bg-green-400 transition disabled:opacity-50"
                                >
                                    {sending ? 'Processing...' : 'Add Funds'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Request Payment Modal */}
            {showRequestModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-[#1a1a1a] border border-premium-gold/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
                    >
                        <h2 className="text-2xl font-serif font-bold text-blue-400 mb-6">Request Payment</h2>
                        <form onSubmit={handleRequestPayment} className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Payer Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-blue-400 outline-none"
                                    placeholder="client@example.com"
                                    value={requestData.payerEmail}
                                    onChange={e => setRequestData({ ...requestData, payerEmail: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Amount</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        step="0.01"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-blue-400 outline-none"
                                        placeholder="0.00"
                                        value={requestData.amount}
                                        onChange={e => setRequestData({ ...requestData, amount: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Currency</label>
                                    <select
                                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white focus:border-blue-400 outline-none"
                                        style={{ backgroundColor: '#1a1a1a' }}
                                        value={requestData.currency}
                                        onChange={e => setRequestData({ ...requestData, currency: e.target.value })}
                                    >
                                        <option value="USD" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>USD</option>
                                        <option value="EUR" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>EUR</option>
                                        <option value="GBP" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>GBP</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Description</label>
                                <input
                                    type="text"
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-blue-400 outline-none"
                                    placeholder="What is this payment for?"
                                    value={requestData.description}
                                    onChange={e => setRequestData({ ...requestData, description: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Due Date (Optional)</label>
                                <input
                                    type="date"
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-blue-400 outline-none"
                                    value={requestData.dueDate}
                                    onChange={e => setRequestData({ ...requestData, dueDate: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowRequestModal(false)}
                                    className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="flex-1 py-3 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-400 transition disabled:opacity-50"
                                >
                                    {sending ? 'Sending...' : 'Send Request'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Invoice Modal */}
            {showInvoiceModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-[#1a1a1a] border border-purple-500/30 rounded-2xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <h2 className="text-2xl font-serif font-bold text-purple-400 mb-6">Create Invoice</h2>
                        <form onSubmit={handleCreateInvoice} className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Client Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-purple-400 outline-none"
                                    placeholder="client@company.com"
                                    value={invoiceData.clientEmail}
                                    onChange={e => setInvoiceData({ ...invoiceData, clientEmail: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Line Items</label>
                                {invoiceData.items.map((item, idx) => (
                                    <div key={idx} className="grid grid-cols-6 gap-2 mb-2">
                                        <input
                                            type="text"
                                            placeholder="Description"
                                            className="col-span-3 bg-white/5 border border-white/10 rounded-lg p-2 text-white text-sm"
                                            value={item.description}
                                            onChange={e => {
                                                const newItems = [...invoiceData.items];
                                                newItems[idx].description = e.target.value;
                                                setInvoiceData({ ...invoiceData, items: newItems });
                                            }}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Qty"
                                            min="1"
                                            className="col-span-1 bg-white/5 border border-white/10 rounded-lg p-2 text-white text-sm"
                                            value={item.quantity}
                                            onChange={e => {
                                                const newItems = [...invoiceData.items];
                                                newItems[idx].quantity = parseInt(e.target.value) || 1;
                                                setInvoiceData({ ...invoiceData, items: newItems });
                                            }}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Price"
                                            step="0.01"
                                            className="col-span-2 bg-white/5 border border-white/10 rounded-lg p-2 text-white text-sm"
                                            value={item.unitPrice}
                                            onChange={e => {
                                                const newItems = [...invoiceData.items];
                                                newItems[idx].unitPrice = e.target.value;
                                                setInvoiceData({ ...invoiceData, items: newItems });
                                            }}
                                        />
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setInvoiceData({ ...invoiceData, items: [...invoiceData.items, { description: '', quantity: 1, unitPrice: '' }] })}
                                    className="text-purple-400 text-sm hover:underline"
                                >
                                    + Add Line Item
                                </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Currency</label>
                                    <select
                                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white outline-none"
                                        style={{ backgroundColor: '#1a1a1a' }}
                                        value={invoiceData.currency}
                                        onChange={e => setInvoiceData({ ...invoiceData, currency: e.target.value })}
                                    >
                                        <option value="USD" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>USD</option>
                                        <option value="EUR" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>EUR</option>
                                        <option value="GBP" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>GBP</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Due Date</label>
                                    <input
                                        type="date"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none"
                                        value={invoiceData.dueDate}
                                        onChange={e => setInvoiceData({ ...invoiceData, dueDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Notes</label>
                                <textarea
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none h-20 resize-none"
                                    placeholder="Payment terms, thank you note, etc."
                                    value={invoiceData.notes}
                                    onChange={e => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                                />
                            </div>
                            <div className="bg-purple-500/10 p-3 rounded-lg text-sm">
                                <span className="text-white/60">Total: </span>
                                <span className="text-purple-400 font-bold">
                                    {invoiceData.items.reduce((sum, item) => sum + (item.quantity * parseFloat(item.unitPrice || '0')), 0).toLocaleString('en-US', { style: 'currency', currency: invoiceData.currency })}
                                </span>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowInvoiceModal(false)}
                                    className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="flex-1 py-3 rounded-lg bg-purple-500 text-white font-bold hover:bg-purple-400 transition disabled:opacity-50"
                                >
                                    {sending ? 'Creating...' : 'Create & Send Invoice'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Escrow Modal */}
            {showEscrowModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-[#1a1a1a] border border-orange-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
                    >
                        <h2 className="text-2xl font-serif font-bold text-orange-400 mb-6">Create Escrow</h2>
                        <form onSubmit={handleCreateEscrow} className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Counterparty Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-orange-400 outline-none"
                                    placeholder="contractor@example.com"
                                    value={escrowData.counterpartyEmail}
                                    onChange={e => setEscrowData({ ...escrowData, counterpartyEmail: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Total Amount</label>
                                    <input
                                        type="number"
                                        required
                                        min="1"
                                        step="0.01"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-orange-400 outline-none"
                                        placeholder="0.00"
                                        value={escrowData.amount}
                                        onChange={e => setEscrowData({ ...escrowData, amount: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Currency</label>
                                    <select
                                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white outline-none"
                                        style={{ backgroundColor: '#1a1a1a' }}
                                        value={escrowData.currency}
                                        onChange={e => setEscrowData({ ...escrowData, currency: e.target.value })}
                                    >
                                        <option value="USD" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>USD</option>
                                        <option value="EUR" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>EUR</option>
                                        <option value="GBP" style={{ backgroundColor: '#1a1a1a', color: 'white' }}>GBP</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase text-white/50 mb-1">Description</label>
                                <input
                                    type="text"
                                    className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white focus:border-orange-400 outline-none"
                                    placeholder="Project or service description"
                                    value={escrowData.description}
                                    onChange={e => setEscrowData({ ...escrowData, description: e.target.value })}
                                />
                            </div>
                            <div className="bg-orange-500/10 p-4 rounded-lg text-xs text-white/60">
                                <p>🔒 Funds will be securely held until milestones are marked complete by both parties.</p>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowEscrowModal(false)}
                                    className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="flex-1 py-3 rounded-lg bg-orange-500 text-white font-bold hover:bg-orange-400 transition disabled:opacity-50"
                                >
                                    {sending ? 'Creating...' : 'Create Escrow'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                <header className="mb-12">
                    <h1 id="financial-title" className="text-4xl font-bold font-serif text-premium-gold mb-2">My Vault</h1>
                    <p className="text-white/60">Manage your earnings, payments, and assets.</p>
                </header>

                {error && (
                    <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg mb-8">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Wallets */}
                    <div id="financial-wallets" className="col-span-2 space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-2xl text-white/90">Wallets</h2>
                            <button
                                id="financial-create-wallet"
                                onClick={handleCreateWallet}
                                className="bg-premium-gold text-black px-4 py-2 rounded-lg font-medium hover:bg-premium-gold/90 transition"
                            >
                                + New Wallet (USD)
                            </button>
                        </div>

                        {loading ? (
                            <p>Loading...</p>
                        ) : wallets.length === 0 ? (
                            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-8 text-center">
                                <p className="text-white/60 mb-4">You don't have any wallets yet.</p>
                                <button className="text-premium-gold hover:underline" onClick={handleCreateWallet}>Created one now</button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {wallets.map(wallet => (
                                    <motion.div
                                        key={wallet.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-xl p-6 relative overflow-hidden group hover:border-premium-gold/30 transition-all"
                                    >
                                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                                            <span className="text-6xl font-serif">{wallet.currency === 'USD' ? '$' : wallet.currency}</span>
                                        </div>
                                        <h3 className="text-sm uppercase tracking-wider text-white/50 mb-1">{wallet.type} Wallet</h3>
                                        <div className="text-3xl font-bold text-premium-gold mb-4">
                                            {parseFloat(wallet.balance).toLocaleString('en-US', { style: 'currency', currency: wallet.currency })}
                                        </div>
                                        <div className="text-xs text-white/40 font-mono">
                                            ID: {wallet.id.substring(0, 8)}...
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Actions */}
                    <div className="col-span-1">
                        <div id="financial-actions" className="bg-white/5 border border-white/10 rounded-xl p-6 h-full">
                            <h3 className="text-lg font-medium text-white/90 mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button
                                    id="financial-send"
                                    onClick={() => setShowTransferModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-premium-gold/20 flex items-center justify-center text-premium-gold">↗</div>
                                    Send Money
                                </button>
                                <button
                                    id="financial-request"
                                    onClick={() => setShowRequestModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">↘</div>
                                    Request Payment
                                </button>
                                <button
                                    id="financial-deposit"
                                    onClick={() => setShowDepositModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">+</div>
                                    Add Funds
                                </button>
                                <button
                                    id="financial-invoice"
                                    onClick={() => setShowInvoiceModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400">📄</div>
                                    Create Invoice
                                </button>
                                <button
                                    id="financial-escrow"
                                    onClick={() => setShowEscrowModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400">🔒</div>
                                    Escrow Funds
                                </button>
                                <button
                                    id="financial-withdraw"
                                    onClick={() => setShowWithdrawModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">⬇</div>
                                    Withdraw Funds
                                </button>
                                <button
                                    id="financial-kyc"
                                    onClick={() => setShowKycModal(true)}
                                    className="w-full text-left p-3 hover:bg-white/5 rounded-lg transition flex items-center gap-3 text-white/70 hover:text-white"
                                >
                                    <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400">🛡</div>
                                    {kycStatus?.level === 'tier3' ? 'KYC VIP' : 'Verify Identity'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Transaction History */}
                    <div id="financial-history" className="col-span-3 mt-8">
                        <h3 className="text-xl font-medium text-white/90 mb-4">Transaction History</h3>
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                            {transactions.length === 0 ? (
                                <div className="p-8 text-center text-white/40">No transactions found.</div>
                            ) : (
                                <table className="w-full text-left">
                                    <thead className="bg-black/20 text-white/50 text-xs uppercase">
                                        <tr>
                                            <th className="p-4">Date</th>
                                            <th className="p-4">Description</th>
                                            <th className="p-4">Status</th>
                                            <th className="p-4 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {transactions.map((tx) => (
                                            <tr key={tx.id} className="hover:bg-white/5 transition">
                                                <td className="p-4 text-white/70 text-sm">
                                                    {new Date(tx.created_at).toLocaleDateString()}
                                                </td>
                                                <td className="p-4 text-white text-sm font-medium">
                                                    {tx.description || 'Transfer'}
                                                </td>
                                                <td className="p-4">
                                                    <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 text-xs capitalize">
                                                        {tx.status}
                                                    </span>
                                                </td>
                                                <td className={`p-4 text-right font-mono font-bold ${parseFloat(tx.amount) > 0 ? 'text-green-400' : 'text-white'}`}>
                                                    {parseFloat(tx.amount) > 0 ? '+' : ''}
                                                    {parseFloat(tx.amount).toLocaleString('en-US', { style: 'currency', currency: tx.currency })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
                {/* Withdrawal Modal */}
                {showWithdrawModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#1a1a1a] border border-red-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl">
                            <h2 className="text-2xl font-serif font-bold text-red-400 mb-6">Withdraw Funds</h2>
                            <form onSubmit={handleWithdraw} className="space-y-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Amount</label>
                                    <input type="number" required min="1" step="0.01" className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none"
                                        value={withdrawalData.amount} onChange={e => setWithdrawalData({ ...withdrawalData, amount: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Provider</label>
                                    <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white outline-none"
                                        value={withdrawalData.provider} onChange={e => setWithdrawalData({ ...withdrawalData, provider: e.target.value, method: e.target.value === 'mfsafrica' ? 'mobile_money' : 'bank_transfer' })}>
                                        <option value="flutterwave">Flutterwave (Bank)</option>
                                        <option value="mfsafrica">MFS Africa (Mobile Money)</option>
                                        <option value="payoneer">Payoneer</option>
                                    </select>
                                </div>

                                {withdrawalData.provider === 'mfsafrica' ? (
                                    <div>
                                        <label className="block text-xs uppercase text-white/50 mb-1">Mobile Number</label>
                                        <input type="tel" required className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none" placeholder="+255..."
                                            value={withdrawalData.details.phoneNumber}
                                            onChange={e => setWithdrawalData({ ...withdrawalData, details: { ...withdrawalData.details, phoneNumber: e.target.value } })} />
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <label className="block text-xs uppercase text-white/50 mb-1">Bank Name</label>
                                            <input type="text" required className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none"
                                                value={withdrawalData.details.accountBank}
                                                onChange={e => setWithdrawalData({ ...withdrawalData, details: { ...withdrawalData.details, accountBank: e.target.value } })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs uppercase text-white/50 mb-1">Account Number</label>
                                            <input type="text" required className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white outline-none"
                                                value={withdrawalData.details.accountNumber}
                                                onChange={e => setWithdrawalData({ ...withdrawalData, details: { ...withdrawalData.details, accountNumber: e.target.value } })} />
                                        </div>
                                    </>
                                )}
                                <div className="flex gap-3 mt-6">
                                    <button type="button" onClick={() => setShowWithdrawModal(false)} className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium">Cancel</button>
                                    <button type="submit" disabled={sending} className="flex-1 py-3 rounded-lg bg-red-500 text-white font-bold hover:bg-red-400 transition">{sending ? 'Processing...' : 'Withdraw'}</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}

                {/* KYC Modal */}
                {showKycModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#1a1a1a] border border-teal-500/30 rounded-2xl p-8 max-w-lg w-full shadow-2xl">
                            <h2 className="text-2xl font-serif font-bold text-teal-400 mb-2">Identity Verification</h2>
                            <p className="text-white/60 text-sm mb-6">Current Level: <span className="text-white font-bold uppercase">{kycStatus?.level || 'Unverified'}</span></p>

                            <form onSubmit={handleKycSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Target Level</label>
                                    <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white outline-none"
                                        value={kycFormData.targetLevel} onChange={e => setKycFormData({ ...kycFormData, targetLevel: e.target.value })}>
                                        <option value="tier1">Tier 1 (Limit: $1,000)</option>
                                        <option value="tier2">Tier 2 (Limit: $10,000)</option>
                                        <option value="tier3">Tier 3 (Unlimited)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Document Type</label>
                                    <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg p-3 text-white outline-none"
                                        value={kycFormData.docType} onChange={e => setKycFormData({ ...kycFormData, docType: e.target.value })}>
                                        <option value="id_card">National ID Card</option>
                                        <option value="passport">Passport</option>
                                        <option value="drivers_license">Driving License</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-white/50 mb-1">Upload Document</label>

                                    {kycFormData.docUrl ? (
                                        <div className="flex items-center justify-between bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <span className="text-teal-400">✓</span>
                                                <span className="text-sm text-teal-300 truncate max-w-[200px]">{kycFormData.docUrl.split('/').pop()}</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setKycFormData({ ...kycFormData, docUrl: '' })}
                                                className="text-white/50 hover:text-white"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <input
                                                type="file"
                                                required
                                                id="kyc-upload"
                                                className="hidden"
                                                onChange={handleKycUpload}
                                                disabled={kycUploading}
                                            />
                                            <label
                                                htmlFor="kyc-upload"
                                                className={`flex items-center justify-center gap-2 w-full bg-white/5 border border-white/10 border-dashed rounded-lg p-4 cursor-pointer hover:bg-white/10 hover:border-teal-500/50 transition ${kycUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <span className="text-2xl">📄</span>
                                                <div className="text-center">
                                                    <p className="text-sm text-white/80 font-medium">
                                                        {kycUploading ? 'Uploading...' : 'Click to select file'}
                                                    </p>
                                                    <p className="text-xs text-white/40 mt-1">Images or PDF (Max 5MB)</p>
                                                </div>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-teal-500/10 p-4 rounded-lg text-xs text-white/60">
                                    <p>ℹ️ Documents are reviewed manually within 24 hours.</p>
                                </div>

                                <div className="flex gap-3 mt-6">
                                    <button type="button" onClick={() => setShowKycModal(false)} className="flex-1 py-3 rounded-lg border border-white/10 hover:bg-white/5 transition font-medium">Cancel</button>
                                    <button type="submit" disabled={sending} className="flex-1 py-3 rounded-lg bg-teal-500 text-white font-bold hover:bg-teal-400 transition">{sending ? 'Submitting...' : 'Submit Review'}</button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default FinancialDashboard;
